import React, { Component, ErrorInfo, ReactNode } from "react";
import { RefreshCw, AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
  title?: string;
  fallbackComponent?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
}

class CarouselErrorBoundary extends Component<Props, State> {
  private maxRetries = 3;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("CarouselErrorBoundary caught an error:", error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log to external service in production
    if (process.env.NODE_ENV === "production") {
      // You can integrate with error tracking services here
      // e.g., Sentry, LogRocket, etc.
    }
  }

  handleRetry = () => {
    if (this.state.retryCount < this.maxRetries) {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1,
      }));
    } else {
      // Reload the page if max retries reached
      window.location.reload();
    }
  };

  handleRefresh = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { title = "Carousel", fallbackComponent } = this.props;
      const { error, retryCount } = this.state;

      // Use custom fallback if provided
      if (fallbackComponent) {
        return fallbackComponent;
      }

      // Determine error type and message
      const isNetworkError = error?.message?.includes("fetch") ||
                           error?.message?.includes("network") ||
                           error?.message?.includes("timeout");

      const isDataError = error?.message?.includes("data") ||
                         error?.message?.includes("undefined") ||
                         error?.message?.includes("null");

      const errorTitle = isNetworkError
        ? `${title} Koneksi Error`
        : isDataError
        ? `${title} Data Error`
        : `${title} Error`;

      const errorMessage = isNetworkError
        ? "Tidak dapat memuat konten. Periksa koneksi internet Anda."
        : isDataError
        ? "Data tidak tersedia atau format tidak valid."
        : "Terjadi kesalahan saat memuat konten.";

      const canRetry = retryCount < this.maxRetries;
      const retryText = canRetry
        ? `Coba Lagi (${retryCount + 1}/${this.maxRetries})`
        : "Refresh Halaman";

      return (
        <div className="w-full">
          {/* Error Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-red-200 dark:border-red-800">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full">
                    <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">
                      {errorTitle}
                    </h3>
                    <p className="text-sm text-red-600 dark:text-red-400">
                      {errorMessage}
                    </p>
                  </div>
                </div>
              </div>

              {/* Error Details */}
              <div className="mb-6">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1">
                      <p className="text-sm text-red-700 dark:text-red-300 mb-2">
                        <strong>Pesan Error:</strong>
                      </p>
                      <p className="text-sm text-red-600 dark:text-red-400 font-mono bg-red-100 dark:bg-red-900/40 p-2 rounded border">
                        {error?.message || "Unknown error occurred"}
                      </p>

                      {retryCount > 0 && (
                        <p className="text-xs text-red-500 dark:text-red-400 mt-2">
                          Percobaan: {retryCount}/{this.maxRetries}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={canRetry ? this.handleRetry : this.handleRefresh}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors font-medium"
                >
                  <RefreshCw className="w-4 h-4" />
                  {retryText}
                </button>

                {canRetry && (
                  <button
                    onClick={this.handleRefresh}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg transition-colors"
                  >
                    Refresh Halaman
                  </button>
                )}
              </div>

              {/* Help Text */}
              <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Tips:</strong>
                </p>
                <ul className="text-sm text-blue-600 dark:text-blue-400 mt-1 space-y-1">
                  <li>• Periksa koneksi internet Anda</li>
                  <li>• Tunggu beberapa saat dan coba lagi</li>
                  <li>• Refresh halaman jika masalah berlanjut</li>
                  {isNetworkError && (
                    <li>• Pastikan server dapat diakses</li>
                  )}
                </ul>
              </div>

              {/* Development Info */}
              {process.env.NODE_ENV === "development" && this.state.errorInfo && (
                <details className="mt-4 p-3 bg-gray-50 dark:bg-gray-900 border rounded-lg">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Debug Info (Development Only)
                  </summary>
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-mono whitespace-pre-wrap bg-gray-100 dark:bg-gray-800 p-2 rounded border max-h-40 overflow-y-auto">
                    {this.state.errorInfo.componentStack}
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default CarouselErrorBoundary;

// Hook version for functional components
export const useCarouselErrorHandler = () => {
  const handleError = React.useCallback((error: Error, errorInfo?: ErrorInfo) => {
    console.error("Carousel Error:", error, errorInfo);

    // You can add custom error handling logic here
    // e.g., send to analytics, show toast, etc.

    return {
      title: "Carousel Error",
      message: error.message,
      canRetry: true,
    };
  }, []);

  return { handleError };
};

// HOC version
export const withCarouselErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  title?: string
) => {
  const WrappedComponent = (props: P) => (
    <CarouselErrorBoundary title={title}>
      <Component {...props} />
    </CarouselErrorBoundary>
  );

  WrappedComponent.displayName = `withCarouselErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
};
